/**
 * Header 5015 — KUK landing-page site header.
 *
 * White bar: logo left; a green WhatsApp "Reach Out" link, a phone line and an
 * Apply CTA right. The WhatsApp link and phone line are desktop-only, as in the
 * reference. Ported from the landing-page branch of kuk-campus-portal's
 * `components/organisms/Header.tsx`, which decides what to show by sniffing the
 * URL; here every control is an explicit toggle.
 *
 * Every field maps to a column that already exists on `header` — the variant
 * adds no schema of its own beyond widening `header.variant`.
 */

export const HEADER_5015_CONSTANTS = {
  HEADER_FIELDS_CONFIG: [
    {
      fieldName: "background_color",
      label: "Bar background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "show_logo",
      label: "Show the logo",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "logo_image",
      label: "Crest (the small mark, left of the wordmark)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the university logo",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "logo",
      label: "Wordmark (beside the crest)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the university wordmark",
      description:
        "The reference pairs a small crest with a wider wordmark. The wordmark is hidden between 768px and 1080px, as it is on the reference's tablet bar.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "logo_alt_text",
      label: "Logo alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "link_href",
      label: "Logo link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Optional — e.g. /",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "whatsapp_show",
      label: "Show the WhatsApp link",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Desktop only, as in the reference",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "whatsapp_button_text",
      label: "WhatsApp text",
      value: "Reach Out",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Reach Out",
      required: false,
      defaultValue: "Reach Out",
    },
    {
      fieldName: "whatsapp_number",
      label: "WhatsApp number",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 918484064675",
      description: "Turned into a wa.me link when no explicit link is set below.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "whatsapp_link",
      label: "WhatsApp link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Optional — overrides the number",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "whatsapp_icon",
      label: "WhatsApp icon (override)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Leave empty for the built-in WhatsApp mark",
      description:
        "The bar ships the reference's own green WhatsApp mark. Upload here only to replace it.",
      required: false,
      defaultValue: "whatsapp",
    },
    {
      fieldName: "whatsapp_button_text_color",
      label: "WhatsApp text colour",
      value: "#61D76A",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#61D76A",
      description: "The reference uses a borderless green text link, not a filled button.",
      required: false,
      defaultValue: "#61D76A",
    },
    {
      fieldName: "phone_number",
      label: "Phone number",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. +91-8484064675",
      description: "Desktop only. Leave empty to hide the phone line.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "phone_text_color",
      label: "Phone colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "pill_background_color",
      label: "Phone disc colour",
      value: "#9F1D33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1D33",
      description: "Fill of the round phone badge — not the number's text colour.",
      required: false,
      defaultValue: "#9F1D33",
    },
    {
      fieldName: "phone_icon",
      label: "Phone icon (override)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Leave empty for the built-in phone disc",
      description:
        "The bar ships the reference's red disc with a white handset. Upload here only to replace it.",
      required: false,
      defaultValue: "phone",
    },
    {
      fieldName: "phone_icon_alt",
      label: "Phone icon alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "show_apply_button",
      label: "Show the Apply button",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "apply_button_text",
      label: "Apply button text",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Apply Now",
      required: false,
      defaultValue: "Apply Now",
    },
    {
      fieldName: "button_link",
      label: "Apply button link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://…",
      description: "Left empty, the button opens the lead form popup.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "button_color",
      label: "Apply button background",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "button_text_color",
      label: "Apply button text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "sticky_to_top",
      label: "Stick to the top of the page",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "show_bar_shadow",
      label: "Show a shadow under the bar",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "show_on_mobile",
      label: "Show below 768px",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],
};
